import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * Prowler Alert - Ambient warning banner for cloaked enemies nearby
 *
 * Shows a red pulsing banner when an enemy Prowler you can't see (cloaked or
 * off-screen) is within 600u, whatever you're flying. Opt-in, under Menu →
 * Interface; ProwlerAlert.ts decides when.
 *
 * Deliberately does NOT reveal the Prowler's position (that would be a
 * wallhack). Only emits a binary "danger nearby" signal.
 *
 * @element prowler-alert
 */
@customElement('prowler-alert')
export class ProwlerAlert extends LitElement {
  static override styles = [
    tokens,
    css`
      /* Placed by airmash-game's .status-stack (bottom centre, over the
         autopilot banner). display: contents, so the host adds no box of its
         own: with nothing to show, the stack skips it entirely. */
      :host {
        display: contents;
        /* Prowler accent: a component-local danger red, deliberately not a
           theme token — it's used nowhere else. RGB channels so each use sets
           its own alpha. */
        --prowler-rgb: 200, 20, 20;
        /* Declared here rather than inherited from the body — the component
           owns its own typography. Click-through, so no selection or tap rules
           are needed. */
        font-family: var(--font-family);
        pointer-events: none;
      }

      .banner {
        padding: 6px 14px;
        background-color: rgba(var(--prowler-rgb), 0.7);
        border-radius: var(--radius-sm);
        color: var(--color-text-white);
        font-size: var(--font-size-13);
        font-weight: bold;
        letter-spacing: 0.1em;
        text-shadow: var(--shadow-text);
        box-shadow: 0 0 8px rgba(var(--prowler-rgb), 0.6);
        animation: prowler-pulse 2.4s ease-in-out infinite;
      }

      @keyframes prowler-pulse {
        0%,
        100% {
          opacity: 0.7;
        }
        50% {
          opacity: 1;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  override render() {
    if (!this.uiState.prowlerAlertActive) return nothing;
    return html`<div class="banner">PROWLER NEARBY</div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'prowler-alert': ProwlerAlert;
  }
}
